import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Moon, Sun, ArrowRight, Zap, Sparkles } from 'lucide-react';
import HowItWorks from '../components/HowItWorks';
import Features from '../components/Features';
import PricingTeaser from '../components/PricingTeaser';
import ClosingSection from '../components/ClosingSection';
import { useAuth } from '../contexts/AuthContext';

// Shared easing for the "high-quality, non-linear" feel
const EASE = [0.21, 0.47, 0.32, 0.98];

const LandingPage = ({ darkMode, toggleDarkMode }) => {
  const { user } = useAuth();
  return (
    <div className={`min-h-screen overflow-x-hidden ${darkMode ? 'bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white' : 'bg-gradient-to-br from-blue-50 via-white to-purple-50 text-gray-900'}`}>
      {/* Nav */}
      <header className={`sticky top-0 z-30 backdrop-blur-md ${darkMode ? 'bg-slate-900/70 border-slate-800' : 'bg-white/70 border-gray-200'} border-b`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <div className="flex items-center space-x-2">
            <div className={`w-8 h-8 ${darkMode ? 'bg-gradient-to-r from-cyan-400 to-blue-500' : 'bg-gradient-to-r from-blue-500 to-purple-600'} rounded flex items-center justify-center`}>
              <Zap className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold tracking-tight text-lg">ICEDMS</span>
          </div>
          <nav className="flex items-center gap-2 sm:gap-3">
            <button
              onClick={toggleDarkMode}
              aria-label="Toggle theme"
              className={`p-2 rounded ${darkMode ? 'hover:bg-slate-800 text-slate-300' : 'hover:bg-gray-100 text-gray-600'}`}
            >
              {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
            <Link to="/pricing" className={`hidden sm:inline-flex px-3 py-2 rounded text-sm font-medium ${darkMode ? 'text-slate-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}>
              Pricing
            </Link>
            {user ? (
              <Link
                to="/dashboard"
                className={`inline-flex items-center gap-2 px-4 py-2 rounded text-sm font-semibold text-white ${darkMode ? 'bg-gradient-to-r from-cyan-500 to-blue-600' : 'bg-gradient-to-r from-blue-500 to-purple-600'} shadow-lg hover:shadow-xl transition-shadow`}
              >
                Dashboard <ArrowRight className="w-4 h-4" />
              </Link>
            ) : (
              <>
                <Link to="/login" className={`px-3 py-2 rounded text-sm font-medium ${darkMode ? 'text-slate-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}>
                  Log in
                </Link>
                <Link
                  to="/signup?trial=true"
                  className={`px-4 py-2 rounded text-sm font-semibold text-white ${darkMode ? 'bg-gradient-to-r from-cyan-500 to-blue-600' : 'bg-gradient-to-r from-blue-500 to-purple-600'} shadow-lg hover:shadow-xl transition-shadow`}
                >
                  Get Started
                </Link>
              </>
            )}
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="relative isolate">
        {/* Animated gradient glow */}
        <motion.div
          aria-hidden="true"
          initial={{ opacity: 0.3, scale: 0.9 }}
          animate={{ opacity: [0.3, 0.55, 0.3], scale: [0.9, 1.08, 0.9] }}
          transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut' }}
          className="pointer-events-none absolute left-1/2 top-0 -translate-x-1/2 -z-10 w-[700px] max-w-[95vw] h-[420px] rounded-full blur-3xl bg-gradient-to-r from-cyan-500/25 via-blue-500/25 to-purple-500/25"
        />

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16 lg:pt-28 lg:pb-24 text-center">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: EASE }}
            className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium mb-6 ${darkMode ? 'bg-slate-800 text-cyan-300 border border-slate-700' : 'bg-white text-blue-600 border border-gray-200'} shadow-sm`}
          >
            <Sparkles className="w-3.5 h-3.5" />
            Instagram outreach, automated
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.08, ease: EASE }}
            className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] mb-5"
          >
            Turn cold Instagram DMs<br className="hidden sm:block" /> into warm leads
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.16, ease: EASE }}
            className={`text-lg sm:text-xl max-w-2xl mx-auto mb-8 ${darkMode ? 'text-slate-300' : 'text-gray-600'}`}
          >
            icedms sends personalized DMs at scale, follows up on autopilot, and routes
            replies straight to your inbox — human-like, rate-safe, and built for agencies and creators.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.24, ease: EASE }}
            className="flex flex-col sm:flex-row items-center justify-center gap-3"
          >
            <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} className="w-full sm:w-auto">
              <Link
                to="/signup?trial=true"
                className={`w-full sm:w-auto inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full text-base font-semibold text-white ${darkMode ? 'bg-gradient-to-r from-cyan-500 to-blue-600 shadow-lg shadow-cyan-500/25' : 'bg-gradient-to-r from-blue-500 to-purple-600 shadow-lg shadow-blue-500/25'} hover:shadow-xl transition-shadow`}
              >
                Start 3-day free trial <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>
            <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} className="w-full sm:w-auto">
              <Link
                to="/pricing"
                className={`w-full sm:w-auto inline-flex items-center justify-center px-7 py-3.5 rounded-full text-base font-semibold border ${darkMode ? 'border-slate-700 text-slate-200 hover:bg-slate-800' : 'border-gray-300 text-gray-800 hover:bg-gray-50'} transition-colors`}
              >
                See pricing
              </Link>
            </motion.div>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.36 }}
            className={`mt-5 text-sm ${darkMode ? 'text-slate-500' : 'text-gray-500'}`}
          >
            No credit card to start · Cancel anytime
          </motion.p>
        </div>
      </section>

      {/* How it works */}
      <HowItWorks darkMode={darkMode} />

      {/* Features */}
      <Features darkMode={darkMode} />

      {/* Pricing teaser */}
      <PricingTeaser darkMode={darkMode} />

      {/* FAQ + final CTA + footer */}
      <ClosingSection darkMode={darkMode} />
    </div>
  );
};

export default LandingPage;
